import React from "react";
import { MagnifyingGlassIcon, LinkBreak2Icon, ChevronDownIcon } from "@radix-ui/react-icons";
import { Icon } from '@src/components/icon/Icon';
import { CustomTooltip } from '../tooltip/Tooltip';
import cn from 'classnames';
import { controlVariantClasses, controlValueVariantClasses, controlChevronClasses, PILL_VALUE_MAX_CHARS } from '@src/components/node-info/behavior-rules/styles';

import { Input } from "@/components/ui/input";

import {
  Command,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {Command as CommandPrimitive} from 'cmdk';
// LF360-1938: the picker's section/group model, kept in a plain module so the
// default-panel composition can be unit-tested without mounting the popover.
import { buildEverythingSection, findSectionIdForValue, type OptionSource, type PanelGroup, type PickerSection } from '@src/components/combobox/pickerSections';

// Create a safe key for cmdk that doesn't contain special characters
const createSafeKey = (_value: string, index: number): string => {
  // Use index to ensure uniqueness and avoid special characters
  return `option-${index}`;
};

export interface Option {
  readonly value: string
  readonly label: string
  readonly isTaskManagement?: boolean
  readonly isAdherence?: boolean
  // LF360-1828: Participant Profile source. fieldType carries the source
  // field's inputType (single|multi|input|picker|date|time) so BehaviorRulesNodeInfo
  // can seed the right operator set + value control on selection.
  readonly isProfileField?: boolean
  readonly fieldType?: string
  // LF360-1828: Onboarding Responses source. Same fieldType vocabulary/rationale
  // as isProfileField above, keyed by the onboarding question's own id instead.
  readonly isOnboardingResponse?: boolean
}

/**
 * LF360-1938: questions belonging to one *other* scenario.
 *
 * This replaced a flat `Option[]`. `fetchAllMessages` has always returned a
 * `scenarioId` alongside each message, but BehaviorRulesNodeInfo used to throw
 * it away and pour every other scenario's questions into a single
 * "Other scenarios" bucket - so two scenarios owning the same question text
 * were indistinguishable, and the list grew unusable as the project grew.
 */
export interface ScenarioOptionGroup {
  readonly scenarioId: string
  readonly scenarioName: string
  readonly items: Option[]
}

export interface SelectParams {
  readonly period?: number
  readonly isTaskManagement?: boolean
  readonly isAdherence?: boolean
  readonly isProfileField?: boolean
  readonly isOnboardingResponse?: boolean
  readonly fieldType?: string
}

/**
 * LF360-1938: kept as an explicit per-source switch rather than spreading every
 * flag off the Option. Downstream (LF360-1812 adherence, LF360-1828 profile /
 * onboarding) seeds its operator set and value control from exactly these
 * params, so each source must keep emitting the same shape it emitted before
 * the sidebar refactor - including which keys are absent entirely.
 */
const buildSelectParams = (source: OptionSource, option: Option, period?: number): SelectParams => {
  switch (source) {
    case 'task':
      return { period, isTaskManagement: true };
    case 'metric':
      return { period, isAdherence: true };
    case 'profile':
      return { period, isProfileField: true, fieldType: option.fieldType };
    case 'onboarding':
      return { period, isOnboardingResponse: true, fieldType: option.fieldType };
    case 'scenario':
    default:
      return { period, isTaskManagement: option.isTaskManagement };
  }
};

export interface ValueRendererProps {
  readonly children?: React.ReactNode
}
const DefaultValueRenderer = ({ children }: ValueRendererProps) => (
  <div className="w-full flex flex-wrap items-center">
    {children}
  </div>
);

const BehaviorFilterInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
    /** LF360-1938: right-aligned slot in the search row, for the clear control. */
    readonly trailing?: React.ReactNode
  }
>(({ className, trailing, ...props }, ref) => (
  <div className="flex items-center border-b px-3">
    <MagnifyingGlassIcon className="mr-2 h-6 w-6 shrink-0 opacity-50" />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        "flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    />
    {trailing}
  </div>
));
BehaviorFilterInput.displayName = CommandPrimitive.Input.displayName;

export interface ComboboxBehaviorProps {
  readonly value?: {
    readonly criteriaValue: string,
    readonly period: number,
    readonly isTaskManagement: boolean
  }
  readonly options?: {
    readonly currentScenario: Option[],
    // LF360-1938: grouped per scenario - see ScenarioOptionGroup.
    readonly otherScenarios: ScenarioOptionGroup[],
    readonly taskManagements: Option[],
    // Optional (not required) so ComboboxBehavior stays buildable/usable by
    // any consumer that doesn't seed a Metrics group yet; BehaviorRulesNodeInfo
    // (LF360-1812) supplies it.
    readonly metrics?: Option[],
    // LF360-1828: same optionality rationale as metrics - BehaviorRulesNodeInfo
    // supplies the project's profile fields.
    readonly profileFields?: Option[],
    // LF360-1828: same optionality rationale as metrics/profileFields -
    // BehaviorRulesNodeInfo supplies the project's onboarding questions.
    readonly onboardingResponses?: Option[],
  }
  readonly placeholder?: string;
  readonly onValueChange?: (value: string, params?: SelectParams) => void,
  readonly Renderer?: (props: ValueRendererProps) => React.ReactNode
  readonly disabled?: boolean
  readonly open?: boolean
  readonly onOpenChange?: (open: boolean) => void
  /** LF360-1823: opt-in pill skin. Absent = unchanged default trigger. */
  readonly variant?: 'pill'
}
export function ComboboxBehavior({
    value,
    options,
    placeholder,
    onValueChange,
    Renderer=DefaultValueRenderer,
    disabled = false,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    variant,
  }: ComboboxBehaviorProps) {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const [search, setSearch] = React.useState("");
  // Which sidebar entry the user last picked. `null` means no choice yet, which
  // is the default and shows every source at once. Deliberately *not* reconciled
  // when the spotlight prunes the choice away - see activeSection below.
  const [selectedSectionId, setSelectedSectionId] = React.useState<string | null>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);
  // LF360-1951: true once the user has steered the sidebar themselves in this
  // open session, which stops the preselect effect below from overruling them
  // when the options finish loading. Reset when the popover closes.
  const sectionSteeredByUserRef = React.useRef(false);
  /**
   * LF360-1951: scrolls the sidebar to whichever entry is active. A project
   * with many scenarios overflows the sidebar, so the entry the picker opened
   * on can sit below the fold and leave the panel looking unexplained.
   *
   * A callback ref rather than an effect on `activeSection.id`: the entry is
   * only in the DOM while the popover is open, and only the attach callback
   * knows the node is there. An effect had to guess at that ordering and fired
   * before the sidebar existed.
   */
  const activeSectionRef = React.useCallback((node: HTMLButtonElement | null) => {
    const sidebar = node?.closest<HTMLElement>('[data-behavior-sidebar]');
    // null is the detach call; jsdom lays nothing out, so every measurement
    // below is zero there and this is a no-op rather than a crash.
    if (!node || !sidebar) return;

    // Offset geometry rather than scrollIntoView or viewport rects, and run
    // synchronously rather than inside a requestAnimationFrame. Two reasons:
    // Radix positions the popover a frame or two after mount, so anything
    // measured against the viewport reads as "already visible" and does
    // nothing; and a rAF never fires at all while the tab is in the
    // background, which skipped the scroll silently. Offsets inside the
    // sidebar are settled the moment the ref attaches, whatever the popover is
    // still doing.
    const entryTop = node.offsetTop - sidebar.offsetTop;
    const entryBottom = entryTop + node.offsetHeight;
    const viewTop = sidebar.scrollTop;
    const viewBottom = viewTop + sidebar.clientHeight;
    const margin = 8;

    if (entryTop < viewTop + margin) {
      sidebar.scrollTop = entryTop - margin;
    } else if (entryBottom > viewBottom - margin) {
      sidebar.scrollTop = entryBottom - sidebar.clientHeight + margin;
    }
  }, []);
  // LF360-1951: which row cmdk's cursor sits on. Controlled - see <Command>.
  const [highlightedKey, setHighlightedKey] = React.useState<string | undefined>(undefined);

  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange !== undefined ? controlledOnOpenChange : setInternalOpen;
  const { criteriaValue, period } = value ?? {};
  const currentScenarioOptions = options?.currentScenario ?? [];
  const otherScenarioGroups = options?.otherScenarios ?? [];
  const taskManagementsOptions = options?.taskManagements ?? [];
  const metricsOptions = options?.metrics ?? [];
  const profileFieldsOptions = options?.profileFields ?? [];
  const onboardingResponsesOptions = options?.onboardingResponses ?? [];

  /**
   * LF360-1938: the sidebar's entries, in the order the 2026-09-02 stand-up
   * settled on. Empty sources are omitted rather than rendered blank.
   */
  const sections = React.useMemo<PickerSection[]>(() => {
    const built: PickerSection[] = [];

    const addSource = (id: string, label: string, dataValue: string, source: OptionSource, items: Option[]) => {
      if (!items.length) return;
      built.push({ id, label, isScenario: false, groups: [{ key: id, dataValue, source, items }] });
    };

    // The dataValue slugs for tasks/metrics/current scenario are the ones cmdk
    // used to derive from the old headings - kept verbatim so existing DOM
    // selectors survive the layout change.
    addSource('current', 'This scenario', 'current scenario', 'scenario', currentScenarioOptions);
    addSource('metrics', 'Metrics and Events', 'metrics', 'metric', metricsOptions);
    addSource('tasks', 'Tasks', 'tasks', 'task', taskManagementsOptions);
    addSource('profile', 'Participant Profile', 'participant profile', 'profile', profileFieldsOptions);
    addSource('onboarding', 'Onboarding Responses', 'onboarding responses', 'onboarding', onboardingResponsesOptions);

    // "All Scenarios" is every scenario's questions and nothing else - Andrew on
    // the 2026-09-02 stand-up: "тоді це буде все, окрім Metrics and Events".
    // Tasks / Participant Profile / Onboarding Responses are excluded for the
    // same reason: they are not scenarios.
    const everyScenarioGroup: PanelGroup[] = [];
    if (currentScenarioOptions.length) {
      everyScenarioGroup.push({ key: 'current', heading: 'This scenario', dataValue: 'current scenario', source: 'scenario', items: currentScenarioOptions });
    }
    otherScenarioGroups.forEach((group) => {
      if (!group.items.length) return;
      everyScenarioGroup.push({
        key: `scenario:${group.scenarioId}`,
        heading: group.scenarioName,
        dataValue: `scenario:${group.scenarioId}`,
        source: 'scenario',
        items: group.items,
      });
    });

    if (everyScenarioGroup.length) {
      built.push({ id: 'all-scenarios', label: 'All Scenarios', isScenario: true, groups: everyScenarioGroup });
      otherScenarioGroups.forEach((group) => {
        if (!group.items.length) return;
        built.push({
          id: `scenario:${group.scenarioId}`,
          label: group.scenarioName,
          isScenario: true,
          searchableLabel: group.scenarioName,
          groups: [{ key: `scenario:${group.scenarioId}`, dataValue: `scenario:${group.scenarioId}`, source: 'scenario', items: group.items }],
        });
      });
    }

    return built;
  }, [currentScenarioOptions, otherScenarioGroups, taskManagementsOptions, metricsOptions, profileFieldsOptions, onboardingResponsesOptions]);

  const query = search.trim().toLowerCase();

  /**
   * LF360-1938: the global spotlight. One query narrows the panel *and* drops
   * sidebar entries that keep nothing - Ruslan on the 2026-09-02 stand-up:
   * "якщо там немає метрик зовсім, то ми просто видаляємо зліва в колонки цю
   * опцію".
   */
  const visibleSections = React.useMemo<PickerSection[]>(() => {
    if (!query) return sections;

    return sections
      .map((section) => {
        // A scenario name match keeps that scenario whole, so the user can see
        // everything the named scenario holds rather than only its questions
        // that happen to repeat the name.
        if (section.searchableLabel?.toLowerCase().includes(query)) return section;

        const groups = section.groups
          .map((group) => (
            group.heading?.toLowerCase().includes(query)
              ? group
              : { ...group, items: group.items.filter((option) => option.label?.toLowerCase().includes(query)) }
          ))
          .filter((group) => group.items.length > 0);

        return { ...section, groups };
      })
      .filter((section) => section.groups.length > 0);
  }, [sections, query]);

  /** The default panel, shown until the user picks a sidebar entry. */
  const everythingSection = React.useMemo(() => buildEverythingSection(visibleSections), [visibleSections]);

  // Derived, never stored. With no sidebar choice we show everything; if the
  // spotlight prunes the chosen section we fall back to everything too, rather
  // than jumping the user to an unrelated section. Clearing the query silently
  // restores the original choice because selectedSectionId is never overwritten.
  const activeSection =
    (selectedSectionId === null
      ? undefined
      : visibleSections.find((section) => section.id === selectedSectionId))
    ?? everythingSection;

  /** Whether anything is currently narrowing the panel, so the clear control earns its place. */
  const isFiltered = selectedSectionId !== null || query.length > 0;

  const clearFilters = () => {
    // Counts as steering: after an explicit Clear the picker must stay on
    // everything, not snap back to the selected value's section.
    sectionSteeredByUserRef.current = true;
    setSelectedSectionId(null);
    setSearch('');
    searchRef.current?.focus();
  };

  /**
   * LF360-1951: the sidebar entry that owns the current value. Opening straight
   * on it saves the user re-finding where their own choice lives - a task opens
   * on "Tasks", a question from another scenario on that scenario's entry.
   * `null` (nothing selected) keeps D7's open-on-everything default.
   */
  const sectionIdForValue = React.useMemo(
    () => findSectionIdForValue(sections, criteriaValue),
    [sections, criteriaValue],
  );

  React.useEffect(() => {
    if (!open) {
      sectionSteeredByUserRef.current = false;
      return;
    }
    // Options arrive asynchronously, so this deliberately re-runs as they land
    // rather than only on the open edge - until the user steers, at which point
    // their choice stands for the rest of the session.
    if (sectionSteeredByUserRef.current) return;
    setSelectedSectionId(sectionIdForValue);
  }, [open, sectionIdForValue]);


  /** Every option currently reachable in the panel, in render order. */
  const activeOptions = React.useMemo(
    () => (activeSection?.groups ?? []).flatMap((group) => group.items.map((option) => ({ group, option }))),
    [activeSection],
  );

  // Create a mapping from safe keys to actual values.
  // LF360-1938: keyed off the rendered panel alone. The previous scheme derived
  // an index by summing the lengths of every preceding group, which does not
  // survive a variable number of per-scenario groups.
  const keyToValueMap = React.useMemo(() => {
    const map = new Map<string, string>();
    activeOptions.forEach(({ option }, idx) => {
      map.set(createSafeKey(option.value, idx), option.value);
    });
    return map;
  }, [activeOptions]);

  /**
   * LF360-1951: cmdk's key for the row holding the current value, if the active
   * panel shows it at all. `undefined` lets cmdk fall back to its own default
   * (the first row), which is right when the value lives in another section or
   * nothing is selected.
   */
  const highlightKeyForValue = React.useMemo(() => {
    if (!criteriaValue) return undefined;
    const index = activeOptions.findIndex(({ option }) => option.value === criteriaValue);
    return index === -1 ? undefined : createSafeKey(criteriaValue, index);
  }, [activeOptions, criteriaValue]);

  React.useEffect(() => {
    if (!open) return;
    // cmdk scrolls its cursor into view itself, so putting the cursor on the
    // current value is also what brings a long list to the right place.
    setHighlightedKey(highlightKeyForValue);
  }, [open, highlightKeyForValue]);

  const searchChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }

  const handleAddOption = () => {
    const optionToSelect = currentScenarioOptions?.find((option) => option.label.toLowerCase().includes(search.toLowerCase()));
    if (options && optionToSelect) {
      onValueChange?.(
        optionToSelect?.value,
        {
          period,
          isTaskManagement: optionToSelect?.isTaskManagement
        }
      );
      setSearch('');
      searchRef.current?.focus();
    }
  };

  const selectOption = (group: PanelGroup, option: Option, selectedKey: string) => {
    const actualValue = keyToValueMap.get(selectedKey) ?? option.value;
    if (criteriaValue !== option.value) {
      onValueChange?.(actualValue, buildSelectParams(group.source, option, period));
    }
    searchRef.current?.focus();
  };

  // TODO: find out what is a hack i've tried to do with this code
  // const isValueFromOptions = (value: string) => {
  //   return currentScenarioOptions?.some(option => option.label === value);
  // }

  const availableOptions = currentScenarioOptions;
  const otherScenariosOptions = React.useMemo(
    () => otherScenarioGroups.flatMap((group) => group.items),
    [otherScenarioGroups],
  );
  // LF360-1812: a subject-level metric (adherence) is a valid selected value too -
  // without this it renders as a dangling reference (broken-link icon + raw value).
  const isMetricValue = !!metricsOptions?.find((option) => option.value === criteriaValue);
  // LF360-1828: same rationale as metrics, for a selected profile field.
  const isProfileFieldValue = !!profileFieldsOptions?.find((option) => option.value === criteriaValue);
  // LF360-1828: same rationale as metrics/profileFields, for a selected onboarding question.
  const isOnboardingResponseValue = !!onboardingResponsesOptions?.find((option) => option.value === criteriaValue);
  const isValidOption =
    !criteriaValue
    || otherScenariosOptions.length === 0 && availableOptions.length === 0
    || !!availableOptions?.find((option) => option.value === criteriaValue)
    || !!otherScenariosOptions?.find((option) => option.value === criteriaValue)
    || !!taskManagementsOptions?.find((option) => option.value === criteriaValue)
    || isMetricValue
    || isProfileFieldValue
    || isOnboardingResponseValue;
  const tooltipItemMessage = isValidOption
    ? (criteriaValue?.length ? criteriaValue : 'Select value...')
    : 'The associated object was deleted';
  const criteriaValueTitle = React.useMemo(() => {
    if (!criteriaValue) return '';

    const option = [...availableOptions, ...otherScenariosOptions, ...taskManagementsOptions, ...metricsOptions, ...profileFieldsOptions, ...onboardingResponsesOptions]
      .find(opt => opt.value === criteriaValue);

    return option?.label ?? criteriaValue;
  }, [criteriaValue, availableOptions, otherScenariosOptions, taskManagementsOptions, metricsOptions, profileFieldsOptions, onboardingResponsesOptions]);
  const pillPlaceholder = placeholder ?? 'Select an option...';
  // LF360-1823 Finding A: how many characters the pill's native <input>
  // should report via its `size` attribute so it content-sizes instead of
  // falling back to the browser's ~20-character default - see the pill
  // branch below. A ternary on `.length`, not `||`, to match this file's
  // existing tooltipItemMessage pattern above and avoid a falsy-string trap
  // (`criteriaValueTitle` can legitimately be '').
  //
  // LF360-1823 C1: clamped at PILL_VALUE_MAX_CHARS. Without this, a
  // sentence-length scenario question (the Figma itself shows
  // "How do you feel?") or a dangling reference's raw 36-char UUID drives
  // `size` past what the panel can hold - and once `size` sets that large
  // an intrinsic width, no `max-w-*` on the input can shrink it back down
  // (see controlValueVariantClasses' doc comment in styles.ts). The two
  // caps - this one and the max-w-[Nch] there - guard different things and
  // both are required.
  const pillDisplayLength = Math.min(
    criteriaValueTitle.length > 0 ? criteriaValueTitle.length : pillPlaceholder.length,
    PILL_VALUE_MAX_CHARS,
  );

  const firstScenarioSectionId = visibleSections.find((section) => section.isScenario)?.id;

  return (
    <div>
      <Popover
        open={open}
        onOpenChange={setOpen}
      >
        <Renderer>
          <CustomTooltip content={tooltipItemMessage}>
            <PopoverTrigger
              className={cn(
                controlVariantClasses(variant),
                variant === 'pill' ? 'flex items-center relative' : 'flex items-center w-full relative',
              )}
              disabled={disabled}
            >
              {!isValidOption && <LinkBreak2Icon className="absolute left-2 text-red-500 mr-2"/>}
              {variant === 'pill' ? (
                // LF360-1823 Finding A: the shared <Input> renders a native
                // <input>, a replaced form control whose `width: auto`
                // resolves to the UA-default ~20-character preferred width
                // (driven by the `size` attribute) rather than the value's
                // actual length - unlike Selectbox's plain <div> value, it
                // does not shrink-wrap its text. `size` is the only way to
                // content-size a real <input> without a hardcoded pixel
                // width. This value is never typed into directly (the
                // popover's own search field handles that), so a plain
                // native element in place of the shared <Input> wrapper is
                // safe here and keeps the neutralising classes in one spot.
                <input
                  type="text"
                  readOnly
                  value={criteriaValueTitle}
                  size={Math.max(1, pillDisplayLength)}
                  className={cn(controlValueVariantClasses(variant), { 'pl-8': !isValidOption })}
                  placeholder={pillPlaceholder}
                  disabled={disabled}
                />
              ) : (
                <Input
                  value={criteriaValueTitle}
                  className={cn("justify-between px-2 w-full truncate", { 'pl-8': !isValidOption })}
                  placeholder={placeholder ?? 'Select an option...'}
                  disabled={disabled}
                />
              )}
              {variant === 'pill' && <ChevronDownIcon className={controlChevronClasses()} />}
            </PopoverTrigger>
          </CustomTooltip>
        </Renderer>
        <PopoverContent className="w-[640px] p-0" forceMount={open ? true : undefined}>
          <Command
            shouldFilter={false}
            // LF360-1951: controlled so the picker can open with cmdk's cursor
            // already on the current value instead of on the first row. Left
            // uncontrolled it highlighted row one, which read as "this is your
            // choice" and buried the real one. onValueChange hands the cursor
            // straight back to cmdk, so arrow keys behave exactly as before.
            value={highlightedKey}
            onValueChange={setHighlightedKey}
          >
            <BehaviorFilterInput
              ref={searchRef}
              value={search}
              onChangeCapture={searchChangeHandler}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddOption();
                }
              }}
              placeholder="Find..."
              className="h-9 my-2"
              trailing={isFiltered && (
                <button
                  // Inside BehaviorRulesNodeInfo's <form>, same as the sidebar
                  // buttons - without this a click would submit it.
                  type="button"
                  data-testid="combobox=>behavior:clear-filters"
                  onClick={clearFilters}
                  className="ml-2 shrink-0 rounded-sm px-2 py-1 text-xs text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground"
                >
                  Clear filters
                </button>
              )}
            />
            <div className="flex max-h-[300px]">
              {/* Left: sidebar. Plain buttons, not CommandItems - these pick a
                  section, they are not selectable condition values, and cmdk's
                  keyboard navigation should stay on the panel. */}
              <div data-behavior-sidebar className="w-[200px] shrink-0 overflow-y-auto border-r py-1">
                {visibleSections.map((section) => (
                  <React.Fragment key={section.id}>
                    {/* "Scenarios" is a label, never a target. Making section
                        headings clickable is not this codebase's pattern (the
                        Administration Panel does not) - hence the explicit
                        "All Scenarios" entry beneath it. */}
                    {/* LF360-1938 (design pass): bold and full-strength, the
                        same size as the entries under it. The mockup separates
                        this label by weight, not by shrinking and greying it -
                        muted small caps read as disabled next to real entries. */}
                    {section.id === firstScenarioSectionId && (
                      <div className="px-3 pt-3 pb-1 text-sm font-bold leading-6 text-foreground">
                        Scenarios
                      </div>
                    )}
                    <button
                      // Inside BehaviorRulesNodeInfo's <form>; without this a
                      // click would submit it.
                      type="button"
                      data-testid={`combobox=>behavior:section:${section.id}`}
                      ref={section.id === activeSection?.id ? activeSectionRef : undefined}
                      onClick={() => {
                        sectionSteeredByUserRef.current = true;
                        setSelectedSectionId(section.id);
                        searchRef.current?.focus();
                      }}
                      className={cn(
                        // LF360-1938 (design pass): text-sm with leading-6 and
                        // py-2. The scenario entries are NOT indented - the
                        // mockup aligns every sidebar entry on one left edge and
                        // lets the bold "Scenarios" label do the grouping.
                        'flex w-full items-center rounded-md px-3 py-2 text-left text-sm leading-6',
                        section.id === activeSection?.id
                          ? 'bg-accent text-accent-foreground'
                          : 'hover:bg-accent/50',
                      )}
                    >
                      <span className="truncate">{section.label}</span>
                    </button>
                  </React.Fragment>
                ))}
              </div>

              {/* Right: the selected section's items. */}
              <div className="flex-1 overflow-y-auto">
                {!activeSection && (
                  <div className="p-4 text-sm text-muted-foreground">
                    {query ? 'Nothing matches your search' : 'No options available'}
                  </div>
                )}
                {activeSection?.groups.map((group) => {
                  const body = group.items.map((option) => {
                    const idx = activeOptions.findIndex(
                      (entry) => entry.group.key === group.key && entry.option.value === option.value,
                    );
                    const isValueSelected = criteriaValue === option.value;
                    const safeKey = createSafeKey(option.value, idx);
                    return (
                      <CommandItem
                        key={`${group.key}:${option.value}`}
                        value={safeKey}
                        data-current-value={isValueSelected || undefined}
                        onSelect={(selectedKey) => { selectOption(group, option, selectedKey); }}
                        // LF360-1938 (design pass): py-2 + leading-6 for the
                        // roomier rhythm the mockup has; rounded-md so the
                        // selected row reads as the same pill as the sidebar's.
                        className={cn(
                          'relative flex items-center justify-between rounded-md py-2 leading-6',
                          // LF360-1951: the current value keeps a weight of its
                          // own, so it is still identifiable after the user
                          // arrows the cursor off it. The tint is cmdk's job -
                          // the cursor opens on this row, and a second
                          // background here only competed with it.
                          isValueSelected && 'font-medium',
                        )}
                      >
                        <div className="max-w-full truncate text-sm">
                          {option.label}
                        </div>
                        <div className="w-6 min-w-6">
                          {isValueSelected && <Icon type="check" size="small" />}
                        </div>
                      </CommandItem>
                    );
                  });

                  // A heading only earns its space when the section shows more
                  // than one group - i.e. inside "All Scenarios" and the
                  // default everything panel.
                  //
                  // LF360-1938 (design pass): cmdk's own heading style is
                  // text-xs/font-medium, which the mockup contradicts - there
                  // the group headings are the strongest thing on the panel,
                  // bold and full-strength at the item size, so the eye can
                  // find a scenario without reading it. Overridden here rather
                  // than in @/components/ui/command.tsx, which other pickers
                  // share.
                  const headingClasses = '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-sm [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:leading-6 [&_[cmdk-group-heading]]:text-foreground';

                  return group.heading
                    ? <CommandGroup key={group.key} value={group.dataValue} heading={group.heading} className={headingClasses}>{body}</CommandGroup>
                    : <CommandGroup key={group.key} value={group.dataValue}>{body}</CommandGroup>;
                })}
                {activeSection && activeSection.groups.length === 0 && (
                  <div className="p-4 text-sm text-muted-foreground">No options in this section</div>
                )}
              </div>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
